import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { Conversation } from '../../models/conversation.model';
import { ConversationService, CONVERSATION_CONSTANTS } from '../../services/conversation.service';
import { AuthService, UserRole } from '../../auth/auth.service';
import { MessagesComponent } from '../messages/messages.component';
import { ContactEditComponent } from '../../shared/components/contact-edit/contact-edit.component';
import { config } from '../../config';

interface Channel {
  id: string;
  name: string;
  company_id: string;
  channel_type: string;
  status: string;
  mobile?: string;
}

@Component({
  selector: 'app-conversation',
  standalone: true,
  imports: [CommonModule, FormsModule, MessagesComponent, ContactEditComponent],
  template: `
    <div class="conversation-overlay">
      <div class="conversation-modal">
        <div class="conversation-header">
          <!-- Row 1: contact info + actions -->
          <div class="header-top-row">
            <div class="contact-info clickable" (click)="openContactEdit()" title="Click to edit contact">
              <div class="contact-header">
                <span class="edit-icon-visible">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5Z"></path>
                  </svg>
                </span>
                <div class="contact-details">
                  <h3>{{ conversation.contact?.name || 'Unknown' }}</h3>
                  <p>{{ conversation.contact?.mobile || 'No mobile' }}</p>
                </div>
              </div>
            </div>

            <div class="conversation-actions">
              <div class="status-dropdown">
                <button
                  class="status-button"
                  [ngClass]="conversation.status"
                  (click)="toggleStatusDropdown($event)">
                  {{ conversation.status }}
                  <span class="dropdown-arrow" [class.rotated]="isStatusDropdownOpen">▼</span>
                </button>
                <div class="dropdown-content" [class.show]="isStatusDropdownOpen">
                  <a
                    *ngIf="conversation.status !== 'Active'"
                    (click)="handleStatusChange('Active')"
                    class="dropdown-item">
                    Mark as Active
                  </a>
                  <a
                    *ngIf="conversation.status !== 'Archived'"
                    (click)="handleStatusChange('Archived')"
                    class="dropdown-item">
                    Archive
                  </a>
                  <a
                    *ngIf="conversation.status !== 'Deleted'"
                    (click)="handleStatusChange('Deleted')"
                    class="dropdown-item">
                    Delete
                  </a>
                </div>
              </div>

              <button class="close-button" (click)="handleClose()">✕</button>
            </div>
          </div>

          <!-- Row 2: Channel category chips -->
          <div class="channel-chips-row" *ngIf="channelsLoaded">
            <button
              *ngFor="let cat of channelCategories"
              class="channel-chip"
              [class.channel-chip-selected]="isCategorySelected(cat)"
              [class.channel-chip-active]="isCategoryActive(cat) && !isCategorySelected(cat)"
              [class.channel-chip-disabled]="!isCategoryActive(cat)"
              [disabled]="!isCategoryActive(cat)"
              (click)="onTabClick(cat)">
              <!-- WhatsApp icon -->
              <svg *ngIf="cat === 'WhatsApp'" class="chip-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.843L0 24l6.335-1.652A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.89 0-3.66-.498-5.185-1.367l-.371-.22-3.763.981.999-3.671-.242-.378A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
              </svg>
              <!-- Email icon -->
              <svg *ngIf="cat === 'Email'" class="chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <!-- SMS icon -->
              <svg *ngIf="cat === 'SMS'" class="chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.9 12.22 19.79 19.79 0 0 1 1.85 3.6 2 2 0 0 1 3.84 1.42h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6.11 6.11l1.85-1.85a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <span class="chip-label">{{ cat }}</span>
              <span class="chip-dot" *ngIf="isCategoryActive(cat)"></span>
              <span class="chip-badge" *ngIf="getConversationForCategory(cat)?.unread_count && getConversationForCategory(cat)!.unread_count! > 0">
                {{ getConversationForCategory(cat)!.unread_count! > 9 ? '9+' : getConversationForCategory(cat)!.unread_count }}
              </span>
            </button>
          </div>
          <div class="channel-chips-loading" *ngIf="!channelsLoaded">
            <span class="ap-spinner" style="width:14px;height:14px;"></span>
          </div>
        </div>
        
        <div class="conversation-body">
          <div *ngIf="isLoadingConversations" class="loading-message">
            <span class="ap-spinner"></span>
            Loading conversations...
          </div>
          <div *ngIf="!conversation?.id && !isLoadingConversations" class="error-message">
            Error: Invalid conversation data. Missing conversation ID.
          </div>
          <app-messages
            *ngIf="conversation?.id"
            [conversationId]="conversation.id"
            [contact]="conversation.contact || { id: '', mobile: 'Unknown', name: 'Unknown Contact' }"
            [unreadCount]="conversation.unread_count || 0"
            [channelType]="activeCategory || ''">
          </app-messages>
        </div>
      </div>
    </div>
    
    <!-- Contact Edit Modal -->
    <app-contact-edit
      [isVisible]="showContactEdit"
      [contact]="contactToEdit"
      [fixedCompanyId]="conversation.company_id"
      [showCompanyField]="false"
      (contactSaved)="handleContactSaved($event)"
      (cancelEdit)="closeContactEdit()">
    </app-contact-edit>
  `,
  styles: [`
    .conversation-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(13,18,30,.45);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      padding: 1rem;
      box-sizing: border-box;
    }

    .conversation-modal {
      width: 100%;
      max-width: 900px;
      height: 90%;
      max-height: 800px;
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius-lg, 14px);
      box-shadow: var(--ap-shadow-lg, 0 8px 24px rgba(13,18,30,.12));
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .conversation-header {
      display: flex;
      flex-direction: column;
      padding: 16px 20px 0;
      background: var(--ap-surface, #fff);
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
      gap: 0;
    }

    .header-top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding-bottom: 12px;
    }

    /* Channel category chips */
    .channel-chips-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 0 12px;
      border-top: 1px solid var(--ap-border, #E3E6EB);
      flex-wrap: wrap;
    }

    .channel-chips-loading {
      display: flex;
      align-items: center;
      padding: 10px 0 12px;
      border-top: 1px solid var(--ap-border, #E3E6EB);
    }

    .channel-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 12px 5px 9px;
      border: 1.5px solid var(--ap-border, #E3E6EB);
      border-radius: 99px;
      background: var(--ap-surface, #fff);
      font-size: 13px;
      font-weight: 550;
      color: var(--ap-text-muted, #6B7280);
      cursor: pointer;
      transition: border-color 0.15s, color 0.15s, background 0.15s, box-shadow 0.15s;
      position: relative;
    }

    .channel-chip:hover:not(:disabled) {
      border-color: var(--ap-primary, #2F5BDA);
      color: var(--ap-primary, #2F5BDA);
      background: var(--ap-primary-soft, #E8EEFC);
    }

    /* Has conversation but not currently selected */
    .channel-chip-active {
      border-color: var(--ap-text-soft, #4B5563);
      color: var(--ap-text, #0B1220);
    }

    /* Currently viewed channel */
    .channel-chip-selected {
      border-color: var(--ap-primary, #2F5BDA);
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      font-weight: 600;
      box-shadow: 0 0 0 2px rgba(47,91,218,.14);
    }

    /* No conversation for this category */
    .channel-chip-disabled {
      opacity: 0.4;
      cursor: not-allowed;
      border-color: var(--ap-border, #E3E6EB);
      color: var(--ap-text-muted, #6B7280);
    }

    .chip-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }

    .chip-label {
      line-height: 1;
    }

    /* Active dot — shown when a conversation exists for this category */
    .chip-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
      flex-shrink: 0;
      opacity: 0.7;
    }

    /* Unread badge */
    .chip-badge {
      background: var(--ap-danger, #B42318);
      color: #fff;
      border-radius: 99px;
      min-width: 16px;
      height: 16px;
      font-size: 10px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 4px;
      line-height: 1;
      flex-shrink: 0;
    }

    .contact-info {
      position: relative;
    }

    .contact-header {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .contact-details {
      flex: 1;
    }

    .edit-icon-visible {
      color: var(--ap-text-muted, #6B7280);
      transition: color 0.15s;
      background: transparent;
      padding: 6px;
      border-radius: var(--ap-radius-sm, 6px);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .contact-info.clickable {
      cursor: pointer;
      padding: 10px 14px;
      border-radius: var(--ap-radius, 10px);
      transition: background 0.15s;
      border: 1px solid transparent;
    }

    .contact-info.clickable:hover {
      background: var(--ap-surface-muted, #EEF0F3);
      border-color: var(--ap-border, #E3E6EB);
    }

    .contact-info.clickable:hover .edit-icon-visible {
      color: var(--ap-primary, #2F5BDA);
    }

    .contact-info h3 {
      margin: 0 0 4px 0;
      font-size: 15px;
      font-weight: 600;
      color: var(--ap-text, #0B1220);
    }

    .contact-info p {
      margin: 0;
      color: var(--ap-text-muted, #6B7280);
      font-size: 13px;
    }


    .conversation-actions {
      display: flex;
      align-items: center;
    }

    .status-dropdown {
      position: relative;
      margin-right: 12px;
    }

    .status-button {
      padding: 4px 10px;
      border: 1px solid transparent;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 12px;
      font-weight: 550;
      cursor: pointer;
      display: flex;
      align-items: center;
      text-transform: capitalize;
      gap: 4px;
    }

    .status-button.open {
      background: var(--ap-success-soft, #D1FAE5);
      color: var(--ap-success, #047A55);
      border-color: rgba(4,122,85,.2);
    }

    .status-button.archived {
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      border-color: rgba(47,91,218,.2);
    }

    .status-button.deleted {
      background: var(--ap-danger-soft, #FEE2E2);
      color: var(--ap-danger, #B42318);
      border-color: rgba(180,35,24,.2);
    }

    .dropdown-arrow {
      margin-left: 4px;
      font-size: 0.75em;
    }

    .dropdown-content {
      display: none;
      position: absolute;
      background: var(--ap-surface, #fff);
      min-width: 160px;
      box-shadow: var(--ap-shadow, 0 4px 12px rgba(13,18,30,.10));
      z-index: 1001;
      right: 0;
      border-radius: var(--ap-radius, 10px);
      border: 1px solid var(--ap-border, #E3E6EB);
      overflow: hidden;
    }

    .dropdown-content.show {
      display: block;
    }

    .dropdown-arrow.rotated {
      transform: rotate(180deg);
    }

    .dropdown-item {
      padding: 9px 14px;
      display: block;
      color: var(--ap-text, #0B1220);
      font-size: 13px;
      text-decoration: none;
      cursor: pointer;
    }

    .dropdown-item:hover {
      background: var(--ap-surface-muted, #EEF0F3);
    }

    .close-button {
      background: var(--ap-surface-muted, #EEF0F3);
      border: 1px solid var(--ap-border, #E3E6EB);
      width: 32px;
      height: 32px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 16px;
      cursor: pointer;
      color: var(--ap-text-muted, #6B7280);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }

    .close-button:hover {
      background: var(--ap-danger-soft, #FEE2E2);
      border-color: rgba(180,35,24,.2);
      color: var(--ap-danger, #B42318);
    }

    .conversation-body {
      flex: 1;
      overflow: hidden;
      position: relative;
      background: var(--ap-bg, #F6F7F9);
    }

    .loading-message {
      padding: 20px;
      text-align: center;
      color: var(--ap-text-muted, #6B7280);
      font-style: italic;
    }

    .error-message {
      padding: 16px;
      text-align: center;
      color: var(--ap-danger, #B42318);
      background: var(--ap-danger-soft, #FEE2E2);
      border-radius: var(--ap-radius-sm, 6px);
      margin: 15px;
      font-size: 13px;
    }
    
    /* Tablet Styles (768px - 1024px) */
    @media (max-width: 1024px) {
      .conversation-overlay {
        padding: 0.75rem;
      }
      
      .conversation-modal {
        height: 92%;
        border-radius: 14px;
      }
      
      .conversation-header {
        padding: 18px 20px;
        min-height: 75px;
        gap: 14px;
      }
      
      .contact-info h3 {
        font-size: 1.05rem;
      }
      
      .contact-info p {
        font-size: 0.85rem;
      }
      
      .close-button {
        width: 38px;
        height: 38px;
        font-size: 1.1em;
      }
    }
    
    /* Mobile Styles (max-width: 768px) */
    @media (max-width: 768px) {
      .conversation-overlay {
        padding: 0.5rem;
        align-items: flex-start;
        padding-top: 2rem;
      }
      
      .conversation-modal {
        width: 100%;
        height: 95%;
        max-height: none;
        border-radius: 12px;
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
      }
      
      .conversation-header {
        padding: 16px 18px 0;
      }

      .header-top-row {
        padding-bottom: 10px;
      }

      .contact-info {
        width: 100%;
      }

      .contact-info.clickable {
        padding: 12px 14px;
        border-radius: 10px;
      }

      .contact-info h3 {
        font-size: 1rem;
        margin-bottom: 4px;
      }

      .contact-info p {
        font-size: 0.85rem;
      }

      .channel-chip {
        font-size: 12px;
        padding: 4px 10px 4px 7px;
        gap: 5px;
      }

      .conversation-actions {
        justify-content: space-between;
      }
      
      .status-dropdown {
        margin-right: 0;
      }

      .dropdown-content {
        right: unset;
        left: 0;
      }

      .status-button {
        font-size: 0.85rem;
        padding: 8px 14px;
      }
      
      .close-button {
        width: 36px;
        height: 36px;
        font-size: 1rem;
      }
      
      .edit-icon-visible {
        width: 28px;
        height: 28px;
        font-size: 16px;
        padding: 4px;
      }
      
      .contact-header {
        gap: 10px;
      }
    }
    
    /* Small Mobile Styles (max-width: 480px) */
    @media (max-width: 480px) {
      .conversation-overlay {
        padding: 0.25rem;
        padding-top: 1rem;
      }
      
      .conversation-modal {
        height: 98%;
        border-radius: 10px;
      }
      
      .conversation-header {
        padding: 14px 16px;
        gap: 10px;
      }
      
      .contact-info.clickable {
        padding: 10px 12px;
        border-radius: 8px;
      }
      
      .contact-info h3 {
        font-size: 0.95rem;
        line-height: 1.3;
      }
      
      .contact-info p {
        font-size: 0.8rem;
        line-height: 1.3;
      }
      
      .channel-chip {
        font-size: 11px;
        padding: 4px 9px 4px 7px;
        gap: 4px;
      }

      .status-button {
        font-size: 0.8rem;
        padding: 6px 12px;
      }
      
      .close-button {
        width: 34px;
        height: 34px;
        font-size: 0.9rem;
      }
      
      .dropdown-item {
        padding: 8px 12px;
        font-size: 0.85rem;
      }
    }
    
    /* Extra Small Mobile (max-width: 320px) */
    @media (max-width: 320px) {
      .conversation-overlay {
        padding: 0.1rem;
        padding-top: 0.5rem;
      }
      
      .conversation-modal {
        height: 99%;
        border-radius: 8px;
      }
      
      .conversation-header {
        padding: 12px 14px 0;
      }

      .contact-info h3 {
        font-size: 0.9rem;
        line-height: 1.2;
      }

      .contact-info p {
        font-size: 0.75rem;
        line-height: 1.2;
      }

      .channel-chip {
        font-size: 10px;
        padding: 3px 8px 3px 6px;
        gap: 4px;
      }

      .status-button {
        font-size: 0.75rem;
        padding: 5px 10px;
      }
      
      .close-button {
        width: 32px;
        height: 32px;
        font-size: 0.85rem;
      }
      
      .edit-icon-visible {
        width: 26px;
        height: 26px;
        font-size: 14px;
        padding: 3px;
      }
      
      .contact-header {
        gap: 8px;
      }
    }
    
    /* Large Desktop Styles (min-width: 1200px) */
    @media (min-width: 1200px) {
      .conversation-overlay {
        padding: 1.5rem;
      }
      
      .conversation-modal {
        max-width: 1000px;
        height: 85%;
        border-radius: 18px;
      }
      
      .conversation-header {
        padding: 24px 28px 0;
      }

      .header-top-row {
        padding-bottom: 14px;
      }

      .contact-info.clickable {
        padding: 14px 18px;
      }

      .contact-info h3 {
        font-size: 1.15rem;
      }

      .contact-info p {
        font-size: 0.95rem;
      }

      .channel-chip {
        font-size: 14px;
        padding: 6px 14px 6px 10px;
        gap: 7px;
      }

      .close-button {
        width: 44px;
        height: 44px;
        font-size: 1.3em;
      }
      
      .edit-icon-visible {
        width: 36px;
        height: 36px;
        font-size: 20px;
        padding: 8px;
      }
      
      .contact-header {
        gap: 14px;
      }
    }
  `]
})
export class ConversationComponent implements OnInit, OnDestroy {
  @Input() conversation!: Conversation;
  @Output() close = new EventEmitter<void>();
  
  public showContactEdit: boolean = false;
  public contactToEdit: any = null;
  public availableConversations: Conversation[] = [];
  public selectedConversationId?: string;
  public isLoadingConversations: boolean = false;
  public isStatusDropdownOpen: boolean = false;
  
  // Channel category tabs
  private _channels: Channel[] = [];
  public channelsLoaded: boolean = false;
  public readonly channelCategories: string[] = ['SMS', 'Email', 'WhatsApp'];
  public activeCategory: string | null = null;

  private subscriptions = new Subscription();
  private channelTypeLookup: { [channelId: string]: string } = {};
  
  constructor(
    private conversationService: ConversationService,
    private http: HttpClient,
    private authService: AuthService
  ) {}

  // Check if this is a read-only conversation (RC viewing MC conversation)
  public isReadOnlyConversation(): boolean {
    const currentUser = this.authService.currentUserValue;
    const isRCUser = UserRole.OWNER === currentUser?.role &&
      (!currentUser?.company_type || currentUser.company_type === 'RC');
    return isRCUser && this.conversation?.isReadOnly === true;
  }
  
  ngOnInit(): void {
    // Load channel information for display names
    this.loadChannels();
    
    // Validate that we have a conversation
    if (!this.conversation) {
      console.error('ConversationComponent requires a conversation input');
      return;
    }
    
    // Initialize with the provided conversation
    this.initializeSingleConversation();
    
    // Add click listener to close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }
  
  private initializeSingleConversation(): void {
    // Validate the conversation data
    if (!this.conversation) {
      console.error('No conversation provided to ConversationComponent');
      return;
    }
    
    // Ensure the conversation has an ID
    if (!this.conversation.id) {
      console.error('Conversation is missing ID:', this.conversation);
      console.error('Available conversation properties:', Object.keys(this.conversation));
      
      // Try to recover by checking for _id field
      if ((this.conversation as any)._id) {
        console.log('Found _id field, using it as id');
        this.conversation.id = (this.conversation as any)._id.toString();
      } else {
        console.error('No usable ID found in conversation data');
        return; // Exit early if no ID can be found
      }
    }
    
    this.selectedConversationId = this.conversation.id;
    this.setupConversationSubscriptions();
    
    // Load all conversations for this contact to populate the selector
    if (this.conversation.initiator_id) {
      this.loadConversationsForContact(this.conversation.initiator_id);
    }
  }
  
  private loadConversationsForContact(contactId?: string): void {
    if (!contactId) return;
    
    this.isLoadingConversations = true;
    const currentConversationId = this.conversation?.id;
    
    this.subscriptions.add(
      this.conversationService.getConversationsByContactId(contactId).subscribe({
        next: (conversations) => {
          this.availableConversations = conversations;
          this.isLoadingConversations = false;
          
          if (conversations.length > 0) {
            // Ensure the current conversation is in the list and properly selected
            const currentConversation = conversations.find(conv => conv.id === currentConversationId);
            if (currentConversation) {
              // Update the current conversation with any changes from the server
              this.conversation = currentConversation;
              this.selectedConversationId = currentConversation.id;
              console.log('[ConversationComponent] Found current conversation in contact conversations list');
            } else if (currentConversationId) {
              // Current conversation not in list - add it
              console.log('[ConversationComponent] Current conversation not in list, adding it');
              this.availableConversations.unshift(this.conversation);
              this.selectedConversationId = this.conversation.id;
            } else {
              // No current conversation, select the most recent one (first in the list)
              this.switchToConversation(conversations[0]);
            }
          } else {
            console.warn('No conversations found for contact:', contactId);
            // Fallback: just show the current conversation
            if (this.conversation) {
              this.availableConversations = [this.conversation];
            }
          }
          
          this.updateActiveCategory();
          console.log('[ConversationComponent] Loaded conversations for contact:', {
            contactId,
            totalConversations: this.availableConversations.length,
            currentConversationId: this.conversation.id
          });
        },
        error: (error) => {
          console.error('Error loading conversations for contact:', error);
          this.isLoadingConversations = false;
          // Fallback: just show the current conversation
          if (this.conversation) {
            this.availableConversations = [this.conversation];
          }
        }
      })
    );
  }
  
  private setupConversationSubscriptions(): void {
    // Subscribe to active conversation updates (includes WebSocket and manual updates)
    this.subscriptions.add(
      this.conversationService.activeConversation$.subscribe(activeConversation => {
        // Only update if this is the same conversation and there's actually an update
        if (activeConversation && activeConversation.id === this.conversation.id) {
          console.log('[ConversationComponent] Received active conversation update:', activeConversation);
          
          // Update the local conversation object with all changes
          this.conversation = { 
            ...this.conversation, 
            ...activeConversation 
          };
          
          console.log(`[ConversationComponent] Updated conversation:`, {
            status: this.conversation.status,
            contactName: this.conversation.contact?.name,
            contactMobile: this.conversation.contact?.mobile
          });
        }
      })
    );
    
    // Also subscribe to general conversation updates (WebSocket status changes, etc.)
    // This catches updates that come from other sources like message reactivation
    this.subscriptions.add(
      this.conversationService.conversationUpdated$.subscribe(updatedConversation => {
        // Check if this update is for the current conversation
        if (updatedConversation.id === this.conversation.id) {
          console.log('[ConversationComponent] Received conversation status update from WebSocket:', updatedConversation);
          
          // Update the conversation properties directly
          if (updatedConversation.status && updatedConversation.status !== this.conversation.status) {
            console.log(`[ConversationComponent] Status changed from ${this.conversation.status} to ${updatedConversation.status}`);
            this.conversation.status = updatedConversation.status;
          }
          
          // Update contact info if provided
          if (updatedConversation.contact) {
            this.conversation.contact = updatedConversation.contact;
            console.log(`[ConversationComponent] Updated conversation contact info`);
          }
          
          // Update metadata if provided
          if (updatedConversation.metadata) {
            this.conversation.metadata = updatedConversation.metadata;
          }
        }
        
        // Also check if this update affects any conversation in our selector list
        if (this.availableConversations.length > 0) {
          const conversationIndex = this.availableConversations.findIndex(conv => conv.id === updatedConversation.id);
          if (conversationIndex !== -1) {
            // Update the conversation in our available conversations list
            this.availableConversations[conversationIndex] = {
              ...this.availableConversations[conversationIndex],
              ...updatedConversation
            };
            // If this is the current conversation, ensure selector shows it as selected
            if (updatedConversation.id === this.conversation.id) {
              this.selectedConversationId = this.conversation.id;
            }
            console.log('[ConversationComponent] Updated conversation in availableConversations list');
          }
        }
      })
    );
  }
  
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }
  
  private onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.status-dropdown')) {
      this.isStatusDropdownOpen = false;
    }
  }
  
  private loadChannels(): void {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser?.company_id) {
      console.warn('[ConversationComponent] No company ID available for loading channels');
      return;
    }
    
    this.subscriptions.add(
      this.http.get<any>(`${config.apiUrl}/api/channels?company_id=${currentUser.company_id}`).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this._channels = response.data;
            // Create a lookup map for channel types
            this.channelTypeLookup = {};
            response.data.forEach((channel: Channel) => {
              if (channel.id && channel.channel_type) {
                this.channelTypeLookup[channel.id] = channel.channel_type;
              }
            });
            this.channelsLoaded = true;
            this.updateActiveCategory();
            console.log('[ConversationComponent] Loaded channel types:', this.channelTypeLookup);
          } else {
            console.error('Failed to load channels:', response.message);
          }
        },
        error: (error) => {
          console.error('Error loading channels:', error);
        }
      })
    );
  }
  
  public handleClose(): void {
    this.close.emit();
  }
  
  public toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
  }
  
  public handleStatusChange(newStatus: string): void {
    this.isStatusDropdownOpen = false; // Close dropdown after selection
    this.subscriptions.add(
      this.conversationService.updateConversationStatus(this.conversation.id, newStatus).subscribe({
        next: (updatedConversation) => {
          console.log(`Conversation status updated to: ${newStatus}`);
          this.conversation.status = updatedConversation.status;
          
          // Update the conversation in the available conversations list if it exists
          // This ensures the selector stays visible and shows the updated status
          if (this.availableConversations.length > 0) {
            const conversationIndex = this.availableConversations.findIndex(conv => conv.id === this.conversation.id);
            if (conversationIndex !== -1) {
              this.availableConversations[conversationIndex] = {
                ...this.availableConversations[conversationIndex],
                status: updatedConversation.status
              };
              // Ensure the selector shows the correct conversation as selected
              this.selectedConversationId = this.conversation.id;
              console.log('[ConversationComponent] Updated conversation status in availableConversations array');
            }
          }
          
          // Always notify parent of status change
          this.conversationService.setActiveConversation({...this.conversation});
        },
        error: (error) => {
          console.error('Error updating conversation status:', error);
        }
      })
    );
  }
  
  public openContactEdit(): void {
    // Get contact ID from conversation
    const contactId = this.conversation.initiator_id || this.getContactIdFromMetadata();

    if (contactId) {
      // Fetch full contact data from API
      this.http.get<any>(`${config.apiUrl}/api/contacts/${contactId}`).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.contactToEdit = {
              id: response.data.id || response.data._id,
              first_name: response.data.first_name || '',
              last_name: response.data.last_name || '',
              email: response.data.email || '',
              mobile: response.data.mobile || '',
              company_id: response.data.company_id || this.conversation.company_id,
              job_title: response.data.job_title || '',
              notes: response.data.notes || '',
              cv_file_path: response.data.cv_file_path || '',
              cv_file_name: response.data.cv_file_name || '',
              cv_file_size: response.data.cv_file_size || 0,
              cv_uploaded_at: response.data.cv_uploaded_at || ''
            };
            this.showContactEdit = true;
          } else {
            // Fallback to metadata if API fails
            this.contactToEdit = this.getContactFromConversation();
            this.showContactEdit = true;
          }
        },
        error: (err) => {
          console.error('Failed to fetch contact details:', err);
          // Fallback to metadata
          this.contactToEdit = this.getContactFromConversation();
          this.showContactEdit = true;
        }
      });
    } else {
      // No contact ID available, use metadata
      this.contactToEdit = this.getContactFromConversation();
      this.showContactEdit = true;
    }
  }

  private getContactIdFromMetadata(): string {
    if (this.conversation.metadata) {
      try {
        const metadata = JSON.parse(this.conversation.metadata);
        return metadata.contact?.id || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  }
  
  public closeContactEdit(): void {
    this.showContactEdit = false;
    this.contactToEdit = null;
  }
  
  public handleContactSaved(savedContact: any): void {
    // Update the conversation contact information in the UI
    if (this.conversation.contact) {
      this.conversation.contact.name = savedContact.first_name + (savedContact.last_name ? ' ' + savedContact.last_name : '');
      this.conversation.contact.mobile = savedContact.mobile || this.conversation.contact.mobile;
    }
    
    // Update the conversation metadata to reflect the new contact information
    this.updateConversationMetadata(savedContact);
    
    this.closeContactEdit();
  }
  
  private updateConversationMetadata(savedContact: any): void {
    // Parse existing metadata or create new one
    let metadata: any = {};
    
    if (this.conversation.metadata) {
      try {
        metadata = JSON.parse(this.conversation.metadata);
      } catch (e) {
        console.error('Error parsing existing metadata:', e);
        metadata = {};
      }
    }
    
    // Update only essential contact information in metadata
    metadata.contact = {
      id: savedContact.id,
      name: savedContact.first_name + (savedContact.last_name ? ' ' + savedContact.last_name : ''),
      mobile: savedContact.mobile
    };
    
    // Update the conversation metadata on the server
    const updatedMetadata = JSON.stringify(metadata);
    
    this.subscriptions.add(
      this.conversationService.updateConversationMetadata(this.conversation.id, updatedMetadata).subscribe({
        next: (updatedConversation) => {
          // Update local conversation metadata
          this.conversation.metadata = updatedMetadata;
          console.log('Conversation metadata updated successfully');
          
          // Broadcast the conversation update to other sessions
          // This ensures conversation lists in other windows update with new contact info
          const updatedConversationData = {
            ...this.conversation,
            contact: {
              id: savedContact.id,
              name: savedContact.first_name + (savedContact.last_name ? ' ' + savedContact.last_name : ''),
              mobile: savedContact.mobile
            }
          };
          
          // Emit via the conversation service to update lists in other components
          this.conversationService.emitConversationUpdate(updatedConversationData);
        },
        error: (error) => {
          console.error('Error updating conversation metadata:', error);
          // Note: UI is already updated, so this is just a background sync issue
        }
      })
    );
  }
  
  private getContactFromConversation(): any {
    // Try to extract contact ID from metadata
    let contactId = '';
    let firstName = this.conversation.contact?.name || 'Unknown';
    let lastName = '';
    let phone = this.conversation.contact?.mobile || '';
    let email = '';
    
    // Parse metadata to get more contact details
    if (this.conversation.metadata) {
      try {
        const metadata = JSON.parse(this.conversation.metadata);
        if (metadata.contact) {
          contactId = metadata.contact.id || '';
          firstName = metadata.contact.name || metadata.contact.first_name || firstName;
          phone = metadata.contact.mobile || metadata.contact.phone || phone;
          email = metadata.contact.email || '';
        }
      } catch (e) {
        console.error('Error parsing conversation metadata:', e);
      }
    }
    
    // Split name into first and last name if needed
    if (firstName.includes(' ')) {
      const nameParts = firstName.split(' ');
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');
    }
    
    return {
      id: contactId,
      first_name: firstName,
      last_name: lastName,
      mobile: phone,
      email: email,
      company_id: this.conversation.company_id
    };
  }
  
  public onConversationChange(conversationId: string): void {
    console.log('[ConversationComponent] Conversation selector changed to:', conversationId);
    const selectedConversation = this.availableConversations.find(c => c.id === conversationId);
    if (selectedConversation) {
      console.log('[ConversationComponent] Switching to conversation:', selectedConversation);
      this.switchToConversation(selectedConversation);
    } else {
      console.error('[ConversationComponent] Selected conversation not found:', conversationId);
    }
  }
  
  private switchToConversation(conversation: Conversation): void {
    console.log('[ConversationComponent] switchToConversation called with:', {
      newId: conversation.id,
      currentId: this.conversation?.id
    });
    
    // Only clear and recreate subscriptions if they exist
    // This preserves WebSocket subscriptions from ConversationService
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    this.subscriptions = new Subscription();
    
    // Update the conversation
    this.conversation = conversation;
    this.selectedConversationId = conversation.id;
    
    // Setup fresh subscriptions for the new conversation
    this.setupConversationSubscriptions();
    this.updateActiveCategory();

    console.log('[ConversationComponent] Conversation switched successfully');
  }

  private updateActiveCategory(): void {
    if (this.conversation?.channel_id && this.channelTypeLookup[this.conversation.channel_id]) {
      this.activeCategory = this.channelTypeLookup[this.conversation.channel_id];
    } else {
      this.activeCategory = null;
    }
  }

  public getConversationForCategory(category: string): Conversation | null {
    return this.availableConversations.find(conv =>
      conv.channel_id &&
      this.channelTypeLookup[conv.channel_id] === category &&
      conv.status !== 'Deleted'
    ) || null;
  }

  public isCategoryActive(category: string): boolean {
    return this.getConversationForCategory(category) !== null;
  }

  public isCategorySelected(category: string): boolean {
    return this.activeCategory === category;
  }

  public onTabClick(category: string): void {
    if (!this.isCategoryActive(category)) return;
    if (this.isCategorySelected(category)) return;
    const conv = this.getConversationForCategory(category);
    if (conv) {
      this.switchToConversation(conv);
    }
  }

  public getConversationDisplayName(conversation: Conversation): string {
    // Show channel type if available, otherwise show creation date
    const channelType = conversation.channel_id && this.channelTypeLookup[conversation.channel_id] 
      ? ` (${this.channelTypeLookup[conversation.channel_id]})` 
      : '';
    const date = conversation.created_at ? new Date(conversation.created_at).toLocaleDateString() : 'Unknown date';
    return `${date}${channelType}`;
  }
}
